import { cva } from 'class-variance-authority';
import { clsx } from 'clsx';
import { type ComponentPropsWithRef, type ReactNode } from 'react';

import { type AllowedDisplayElements } from '@repo/ui/utils/html';
import { getPolymorphicProps } from '@repo/ui/utils/polymorphic';
import {
  type GapUtilityClass,
  type MarginUtilityClass,
  type PaddingUtilityClass,
} from '@repo/ui/utils/spacing';

import { isEmptyChildren } from '../utils/children.js';

import s from './Flex.module.css';

const DEFAULT_ELEMENT = 'div' as const;

export const flexDisplayPropertyOptions = ['flex', 'inline-flex'] as const;
export type FlexDisplayProperties = (typeof flexDisplayPropertyOptions)[number];

export type Direction = 'row' | 'row-reverse' | 'column' | 'column-reverse';

/**
 * Props specific to the Flex component (not inherited from HTML element)
 */
type FlexOwnProps<T extends AllowedDisplayElements = typeof DEFAULT_ELEMENT> = {
  alignItems?: 'center' | 'flex-start' | 'flex-end' | 'baseline' | 'stretch';
  as?: T;
  children: ReactNode;
  direction?: Direction;
  display?: FlexDisplayProperties;
  gap?: GapUtilityClass | GapUtilityClass[];
  isContainer?: boolean;
  justifyContent?:
    | 'center'
    | 'flex-start'
    | 'flex-end'
    | 'space-between'
    | 'space-around'
    | 'space-evenly';
  margin?: MarginUtilityClass | MarginUtilityClass[];
  padding?: PaddingUtilityClass | PaddingUtilityClass[];
  wrap?: 'wrap' | 'nowrap' | 'wrap-reverse';
  grow?: '0' | '1';
  shrink?: '0' | '1';
};

/**
 * Full Flex props combining own props with HTML element props (including ref).
 * The generic T determines which HTML element's props and ref type to use.
 */
export type FlexProps<
  T extends AllowedDisplayElements = typeof DEFAULT_ELEMENT,
> = FlexOwnProps<T> & Omit<ComponentPropsWithRef<T>, keyof FlexOwnProps<T>>;

export const flexVariants = cva(s.flex, {
  variants: {
    alignItems: {
      center: s.alignItemsCenter,
      ['flex-start']: s.alignItemsFlexStart,
      ['flex-end']: s.alignItemsFlexEnd,
      baseline: s.alignItemsBaseline,
      stretch: s.alignItemsStretch,
    },
    display: {
      flex: s.displayFlex,
      ['inline-flex']: s.displayInlineFlex,
    },
    direction: {
      row: s.row,
      ['row-reverse']: s.rowReverse,
      column: s.column,
      ['column-reverse']: s.columnReverse,
    },
    justifyContent: {
      center: s.justifyContentCenter,
      ['flex-start']: s.justifyContentFlexStart,
      ['flex-end']: s.justifyContentFlexEnd,
      ['space-between']: s.justifyContentSpaceBetween,
      ['space-around']: s.justifyContentSpaceAround,
      ['space-evenly']: s.justifyContentSpaceEvenly,
    },
    wrap: {
      wrap: s.wrap,
      nowrap: s.nowrap,
      ['wrap-reverse']: s.wrapReverse,
    },
    grow: {
      ['0']: s.grow0,
      ['1']: s.grow1,
    },
    shrink: {
      ['0']: s.shrink0,
      ['1']: s.shrink1,
    },
    isContainer: {
      true: s.isContainer,
    },
    // the gap, margin, and padding variants are not implemented as flexVariants, as they use utility classes and not CSS modules.
  },
  defaultVariants: {},
});

export function Flex<T extends AllowedDisplayElements = typeof DEFAULT_ELEMENT>(
  props: FlexProps<T>,
) {
  const {
    alignItems,
    as = DEFAULT_ELEMENT,
    children,
    className,
    direction,
    display = 'flex',
    gap,
    isContainer = false,
    justifyContent,
    margin,
    padding,
    wrap,
    grow,
    shrink,
    ref,
    ...attrs
  } = props;

  // See ADR-008. `0` renders — it paints `"0"`; note `&&` yields its left operand,
  // so `{n && …}` gives `0`, not `false`.
  if (isEmptyChildren(children)) {
    return null;
  }

  const { Component, props: componentProps } = getPolymorphicProps(as, {
    ref,
    className: clsx(
      flexVariants({
        alignItems,
        className,
        direction,
        display,
        isContainer,
        justifyContent,
        wrap,
        grow,
        shrink,
      }),
      gap,
      margin,
      padding,
    ),
    ...attrs,
    children,
  });

  return <Component {...componentProps} />;
}
